import { HStack, Flex, Divider } from '@chakra-ui/layout';
import { Skeleton } from '@chakra-ui/react';
import React from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import { ResolvedLoanFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { percentageToPercentageLabel } from 'packages/common-frontend/utils';

export const LoanPricingOption = ({
  loan,
  loading,
}: {
  loan: ResolvedLoanFragmentGQLType | undefined;
  loading: boolean;
}) => {
  // Not technically correct here - service fee is at the share level
  // But, this is fine for now
  const serviceFee =
    loan?.pricing.margins.find((margin) => margin.serviceFee)?.serviceFee ??
    null;
  return (
    <HStack
      sx={{
        w: '100%',
        gap: '1rem',
        wrap: 'wrap',
        alignItems: 'start',
        py: '.25rem',
      }}
    >
      <Flex fontWeight={500} w="20rem">
        Pricing Option
        {loan && ` (${loan.pricing.name})`}
      </Flex>
      <Flex
        sx={{
          border: '1px solid',
          borderColor: 'gray.200',
          borderRadius: 'md',
          flex: 1,
          flexDirection: 'column',
          gap: '.5rem',
          maxW: '100%',
          p: '1rem',
        }}
      >
        <HStack justifyContent="space-between">
          <Typography fontWeight="medium">All-in-Rate</Typography>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography fontWeight="medium">
              {loan
                ? percentageToPercentageLabel(
                    loan.pricing.current.allInRate * 100,
                    5,
                    true,
                  )
                : '-'}
            </Typography>
          )}
        </HStack>
        <Divider />
        <HStack color={colors.gray[600]} justifyContent="space-between">
          <Typography variant="base-muted">Benchmark</Typography>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography variant="base-muted">
              {loan
                ? commonUtils.toTitleCase(loan.pricing.current.baseRate.name)
                : '-'}
            </Typography>
          )}
        </HStack>
        <HStack justifyContent="space-between">
          <Flex maxW="50%">
            <Typography variant="base-muted">
              Benchmark Determination Date
            </Typography>
          </Flex>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography variant="base-muted">
              {loan
                ? commonDate.fullDateStrToHumanDateString(
                    loan.pricing.current.baseRate.determinationDate,
                  )
                : '-'}
            </Typography>
          )}
        </HStack>
        <HStack color={colors.gray[600]} justifyContent="space-between">
          <Typography variant="base-muted">Benchmark Rate</Typography>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography variant="base-muted">
              {loan
                ? percentageToPercentageLabel(
                    loan.pricing.current.baseRate.publishedRate * 100,
                    5,
                    true,
                  )
                : '-'}
            </Typography>
          )}
        </HStack>
        <HStack color={colors.gray[600]} justifyContent="space-between">
          <Typography variant="base-muted">WA Applicable Margin</Typography>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography variant="base-muted">
              {loan
                ? percentageToPercentageLabel(
                    loan.pricing.current.weightedAverageMarginRate * 100,
                    5,
                    true,
                  )
                : 0}
            </Typography>
          )}
        </HStack>
        <HStack color={colors.gray[600]} justifyContent="space-between">
          <Typography variant="base-muted">Service Fee</Typography>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography variant="base-muted">
              {typeof serviceFee === 'number'
                ? percentageToPercentageLabel(serviceFee * 100, 5, true)
                : '-'}
            </Typography>
          )}
        </HStack>
      </Flex>
    </HStack>
  );
};
